import React, { useEffect, useState } from "react";
import { Form, Button } from "antd";
import StyledForm from "../../components/StyledForm/StyledForm";
import useToken from "../../hooks/useToken";
import useCode from "../../hooks/useCode";
import IToken from "../../interfaces/token";
import {
  API_CONSTS,
  APPLICATION_ID_CONSTS,
  LOGIN_ERRORS,
} from "../../constants";
import { loginUser } from "../../utils/data-fetching/loginUser";
import { loginUserAuthorize } from "../../utils/data-fetching/loginUserAuthorize";
import { getAuthorizeCodes } from "../../utils/data-fetching/getAuthorizeCodes";
import { getUserTokens } from "../../utils/data-fetching/getUserTokens";
import useMSOAppData from "../../hooks/useMSOAppData";
import useRedirectUrlToken from "../../hooks/useRedirectUrlToken";
import useRedirectUrlCode from "../../hooks/useRedirectUrlCode";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import usePageLoading from "../../hooks/usePageLoading";
import LoginFormFields from "../../components/LoginFormFields/LoginFormFields";
import "./LoginView.css";
import { ReactComponent as GoogleLogo } from "../../assets/google-logo.svg";
import useGoogleSSO from "../../hooks/useGoogleSSO";

const LoginFields = (formError: string | null) => (
  <LoginFormFields formError={formError} />
);

const LoginView: React.FC = () => {
  const { applicationId, callbackUrl } = useMSOAppData();
  const [form] = Form.useForm();
  const { saveToken, callbackWithUrlToken } = useToken({
    callbackUrl: callbackUrl!,
  });
  const { saveCode, callbackWithUrlCode } = useCode({
    callbackUrl: callbackUrl!,
  });
  const { isPageLoading, setIsPageLoading } = usePageLoading();
  const [formError, setFormError] = useState<string | null>(null);
  const { googleSSOUrl } = useGoogleSSO({ applicationId });

  const handleFormErrorState = (error: string) => {
    switch (true) {
      case error.includes(LOGIN_ERRORS.INVALID_CREDENTIALS.apiResponse):
        setFormError(LOGIN_ERRORS.INVALID_CREDENTIALS.userErrorMessage);
        break;
      case error.includes(LOGIN_ERRORS.MAX_ATTEMPTS.apiResponse):
        setFormError(LOGIN_ERRORS.MAX_ATTEMPTS.userErrorMessage);
        break;
      default:
        setFormError(LOGIN_ERRORS.DEFAULT.userErrorMessage);
        break;
    }

    setIsPageLoading(false);
  };

  const onFinish = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setFormError(null);
    const queryString = window.location.search;
    const urlSearchParams = new URLSearchParams(queryString);
    setIsPageLoading(true);
    // want to use SSO if the authorize param is in url or if
    // there are no params at all meaning to use the default MSO login flow
    if (
      urlSearchParams.has("authorize") ||
      Array.from(urlSearchParams).length === 0
    ) {
      // loginUserAuthorizeResponse = response status as string value if successful or error string if failed
      const loginUserAuthorizeResponse =
        applicationId &&
        (await loginUserAuthorize({
          username: email,
          password,
          applicationId,
        }));

      if (
        loginUserAuthorizeResponse ===
        API_CONSTS.RETRIEVE_AUTHORIZATION_CODE_RESPONSE
      ) {
        const authorizeResponse =
          applicationId && (await getAuthorizeCodes(applicationId));

        if (!authorizeResponse?.code) {
          // immediately set generic sign in error if no code is returned
          setFormError(LOGIN_ERRORS.DEFAULT.userErrorMessage);
        } else {
          saveCode(authorizeResponse.code);
        }
      } else {
        handleFormErrorState(loginUserAuthorizeResponse?.errorResponse ?? "");
      }
    } else {
      // loginResponse = token data if successful or error object if failed
      const loginResponse =
        applicationId &&
        (await loginUser({ username: email, password, applicationId }));

      if (loginResponse?.tokens?.accessToken) {
        saveToken(loginResponse as IToken);
      } else {
        handleFormErrorState(loginResponse?.errorResponse ?? "");
      }
    }
    setIsPageLoading(false);
  };

  useEffect(() => {
    document.title = "Treez | Sign In";
  }, []);

  useEffect(() => {
    const queryString = window.location.search;
    const urlSearchParams = new URLSearchParams(queryString);
    setIsPageLoading(true);

    if (urlSearchParams.has("code")) {
      const code = urlSearchParams.get("code") || "";
      if (code.length > 0) {
        const tokenData = async () =>
          applicationId && (await getUserTokens({ applicationId, code }));
        tokenData().then((res) => {
          if (res?.accessToken) {
            const tokensObj = {
              tokens: res,
              status: "", // This is added to deal with a ITokens TS error because the status is not returned from the getUserTokens endpoint.
            };
            saveToken(tokensObj);
          }
        });
      }
    } else {
      const codeData = async () =>
        applicationId && (await getAuthorizeCodes(applicationId));
      codeData()
        .then((res) => {
          if (res?.code) {
            saveCode(res?.code);
          }
        })
        .catch((err) => {
          setFormError(LOGIN_ERRORS.DEFAULT.userErrorMessage);
          console.error(`getAuthorizeCode fetch error: ${err}`);
        });
    }
    setIsPageLoading(false);
  }, [applicationId]);

  useRedirectUrlCode({ callbackWithUrlCode });

  // redirect to callback URL for MSO apps when tokens are updated
  useRedirectUrlToken({ callbackWithUrlToken });

  // Only Ask Treez & EPM (plan & brand) should be able to use Google SSO
  const showGoogleButton =
    (applicationId && applicationId?.includes(APPLICATION_ID_CONSTS.EPM)) ||
    applicationId?.includes(APPLICATION_ID_CONSTS.ASK_Treez);

  return (
    <div className="page-wrapper">
      {isPageLoading && <LoadingSpinner />}
      <StyledForm
        formInstance={form}
        pageTitle="Sign in"
        formName="normal_login"
        FormItems={() => LoginFields(formError)}
        onFinish={onFinish}
        formSubmitError={formError}
      />

      {showGoogleButton && (
        <>
          <div className="text-divider">Or continue with</div>
          <Button
            type="default"
            className="icon-button"
            icon={<GoogleLogo />}
            disabled={!googleSSOUrl}
            onClick={() => {
              window.location.assign(googleSSOUrl!);
            }}
          >
            GOOGLE
          </Button>
        </>
      )}
    </div>
  );
};

export default LoginView;
